type tripTypes = 'ONE_WAY' | 'ROUND_TRIP' | 'MULTI_DESTINATION'

type hotelDealTypesTransformed = 'RETAIL' | 'EXPRESS' | 'PRICEBREAKER'

export const FLY_CABIN_CLASS = {
  BUSINESS: 'BUS',
  ECONOMY: 'ECO',
  FIRST: 'FST',
  PREMIUM_ECONOMY: 'PEC'
} as const

export type FLY_CABIN_CLASS_TYPE = keyof typeof FLY_CABIN_CLASS

type OriginDestinationData = {
  originCityData: Readonly<{
    cityID: string
    cityName: string
  }>
  destinationCityData: Readonly<{
    cityID: string
    cityName: string
  }>
  originAirportData: Readonly<{
    airportCode: string
    airportName: string
  }>
  destinationAirportData: Readonly<{
    airportCode: string
    airportName: string
  }>
}

type FlyOriginDestinationData = {
  originDestinationData: Readonly<OriginDestinationData>
  startDate: string | null
}

type StayDestinationData = {
  destinationCityData: Readonly<{
    cityID: string
    cityName: string
  }>
  startDate: string | null
  endDate: string | null
}

type FlySearchData<T = FlyOriginDestinationData> = {
  productType: 'FLY'
  flyCabinClass: FLY_CABIN_CLASS_TYPE
  flyTripType: tripTypes
  flyTravelersCount: Readonly<{
    adultCount: number
    childCount: number
    infantCount: number
  }>
  flyOriginDestinationData: readonly [Readonly<T>, ...ReadonlyArray<T>]
}

type DriveSearchData = {
  productType: 'DRIVE'
  driveTripType: tripTypes
  driveStartDate: string | null
  driveEndDate: string | null
  drivePickupTime: string
  driveDropOffTime: string
  driveOriginDestinationData: Readonly<OriginDestinationData>
}

type StaySearchData<T = StayDestinationData> = {
  productType: 'STAY'
  stayTravelersCount: Readonly<{
    adultCount: number
    childCount: number
    infantCount: number
  }>
  stayRoomCount: number
  stayDestinationData: readonly [Readonly<T>]
}

export type SearchProductData = Readonly<
  FlySearchData | DriveSearchData | StaySearchData
>

type NonNullableReadonlyFields<T, K extends keyof T> = T & {
  readonly [P in K]: NonNullable<T[K]>
}

type MakeStartDateNonNullableReadonly<T extends { startDate: string | null }> =
  NonNullableReadonlyFields<T, 'startDate'>

type StayDestinationDataFiltered =
  MakeStartDateNonNullableReadonly<StayDestinationData>

type FlyOriginDestinationDataFiltered =
  MakeStartDateNonNullableReadonly<FlyOriginDestinationData>

type DriveSearchDataFiltered = NonNullableReadonlyFields<
  DriveSearchData,
  'driveStartDate'
>

export type SearchProductDataFiltered = Readonly<
  | StaySearchData<StayDestinationDataFiltered>
  | FlySearchData<FlyOriginDestinationDataFiltered>
  | DriveSearchDataFiltered
>

type ExpressDealData = {
  __typename: 'TEMP_CDP_ExpressDealHotel' | 'TEMP_CDP_PriceBreakers'
  description: string
  location: string
}
type RetailDealData = {
  __typename: 'TEMP_CDP_RtlHotel'
  averageRating: number
  imageURL: string
  name: string
  neighborhood: string
  numberOfReviews: number
  starRating: number
}

export type AbandonedHotelSelectionApiResponse = {
  hotelURL: string
  checkInDate: string
  checkOutDate: string
  numberOfAdults: number
  numberOfRooms: number
  temp_hotel: RetailDealData | ExpressDealData
}

type AbandonedHotelSelection = {
  headline: string
  subheadline: string
  hotelName?: string
  neighborhoodName?: string
  numReviews?: number
  numStars?: number
  overallRating?: number
  checkInDate: string
  checkOutDate: string
  numAdults: number
  numRooms: number
  imageSrc?: string
  linkUrl: string
  hotelDealType: hotelDealTypesTransformed
  description?: string
  location?: string
  productType: 'hotels'
}

export type AbandonedCarSelectionApiResponse = {
  carURL: string
  averageRating?: number
  carExample: string
  carType: string
  imageURL: string
  isDropOffAtDifferentLocation: boolean
  isOffAirportPickup: boolean
  numberOfReviews?: number
  passengerCapacity: number
  pickupDateTime: string
  pickupLocation: string
  returnDateTime: string
  returnLocation: string
  retailTypeCarCompanyDetails?: {
    brandImageURL: string
  }
}

type AbandonedCarSelection = {
  headline: string
  expressDealText: string
  pickupLocation: string
  isDropOffAtDifferentLocation: boolean
  returnLocation: string
  specifyOffAirport: boolean
  pickUpDate: string
  dropOffDate: string
  numAdults: string
  linkUrl: string
  numReviews?: string
  overallRating?: string
  imageSrc: string
  rcDealType: string
  productType: 'cars'
  brandImg?: string
}

export type AbandonedSelection = AbandonedHotelSelection | AbandonedCarSelection

export type AbandonedSelectionApiResponse =
  | AbandonedHotelSelectionApiResponse
  | AbandonedCarSelectionApiResponse
